import { Dispatch, FC, RefObject, SetStateAction, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import { useInView } from 'react-intersection-observer'
import { GuildIcon } from '@/atoms/Icons/GuildIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { GradientWrapper } from '@/views/TicketsLandingView/NewTicketsLandingView/components/GraidentWrapper'
import {
  ComingSoonTheatricalRelease,
  CurrentTheatricalRelease,
} from '@/views/TicketsLandingView/NewTicketsLandingView/types'
import { canUseGuildCodes } from '@/views/TicketsLandingView/NewTicketsLandingView/utils'

interface Props {
  className?: string
  containerRef: RefObject<HTMLDivElement>
  release: CurrentTheatricalRelease | ComingSoonTheatricalRelease
  isLoopingCard?: boolean
  setSelectedRelease?: Dispatch<SetStateAction<CurrentTheatricalRelease | ComingSoonTheatricalRelease | null>>
}

export const GuildRedemptionCard: FC<Props> = ({
  className,
  containerRef,
  release,
  isLoopingCard,
  setSelectedRelease,
}) => {
  const { t } = useTranslate('tickets')
  const { push } = useSafeRouter()
  const canUseCodes = canUseGuildCodes(release.ticketStatus)
  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    root: containerRef.current,
  })

  const { theatricalSlug, angelKeyArt2x3Image, guildCodes } = release

  useEffect(() => {
    if (isLoopingCard && setSelectedRelease && inView) setSelectedRelease(release)
  }, [inView, isLoopingCard, release, setSelectedRelease])

  const numberOfGuildCodes = guildCodes?.length ?? 0

  const handleCardClick = useCallback(() => {
    if (canUseCodes && guildCodes) return push(`/tickets/${theatricalSlug}?discounts=${guildCodes.join(',')}`)
    window.open(release.hubPageLink, '_blank')
  }, [canUseCodes, guildCodes, push, release.hubPageLink, theatricalSlug])

  return (
    <div>
      <GradientWrapper
        className={classNames(
          'rounded-2xl overflow-hidden max-w-[265px] sm:max-w-[280px] md:max-w-[250px] xl:max-w-[316px]',
          className,
        )}
      >
        <button
          ref={inViewRef}
          disabled={isLoopingCard}
          onClick={handleCardClick}
          className={classNames(
            'relative block group h-[398px] w-full overflow-hidden rounded-2xl border border-transparent hover:border-core-gray-200 md:h-[375px] xl:h-[474px] transition-all ease-in-out',
            inView && 'border-white md:border-transparent',
          )}
        >
          <Image
            src={angelKeyArt2x3Image}
            fill
            alt=""
            className="object-cover object-center transition-transform duration-[500ms] ease-in-out group-hover:scale-[1.02]"
          />

          <div className="absolute inset-0 z-10 hidden items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 md:grid">
            <div className="rounded-xl bg-white/20 p-3 px-6 py-3.5 text-base font-semibold text-white backdrop-blur-[48px]">
              {canUseCodes ? t('redeemTickets', 'Redeem Tickets') : t('learnMore', 'Learn More')}
            </div>
          </div>
        </button>
      </GradientWrapper>

      <div className="mt-3 flex items-center justify-center gap-1 md:mt-6">
        <GuildIcon size={16} className="md:!size-6" color1="core-warning-200" />
        <Text color="core-warning-200" className="photon-caption-sm md:photon-caption-lg">
          {canUseCodes
            ? t('guildTicketsAvailableCount', {
                count: numberOfGuildCodes,
                numberOfGuildCodes,
                defaultValue_one: '{{numberOfGuildCodes}} Guild Ticket Available',
                defaultValue_other: '{{numberOfGuildCodes}} Guild Tickets Available',
              })
            : t('twoGuildTicketsComingSoon', '2 Guild Tickets Coming Soon')}
        </Text>
      </div>
    </div>
  )
}
